import type maplibregl from 'maplibre-gl';

// Duration of the hover grow/shrink tween, in milliseconds.
const HOVER_TWEEN_MS = 150;

export interface FeatureHoverTween {
    /** Ramp the given feature's hover in (and any previously hovered one out). Pass null to ramp all out. */
    setHovered(id: string | null): void;
    /** Cancel any animation and clear all hover feature-state. */
    destroy(): void;
}

/**
 * Smoothly tweens a MapLibre `hoverProgress` feature-state (0→1) on a source so paint
 * expressions can grow the hovered feature — MapLibre applies feature-state changes
 * instantly, so a manual requestAnimationFrame tween is needed for an eased grow.
 *
 * The source must set `promoteId` so the feature ids passed to `setHovered` match. The
 * rAF loop runs only while something is animating; one feature ramps in while the
 * previously hovered one ramps out. All map operations are guarded so a style reload
 * (which drops the source) can't throw.
 */
export function createFeatureHoverTween(map: maplibregl.Map, sourceId: string): FeatureHoverTween {
    const progress = new Map<string, number>();
    const target = new Map<string, number>();
    let hoveredId: string | null = null;
    let rafId: number | null = null;
    let lastTs: number | null = null;

    const setState = (id: string, p: number) => {
        try {
            map.setFeatureState({ source: sourceId, id }, { hoverProgress: p });
        } catch {
            // Source may have been removed during a style reload — ignore.
        }
    };

    // Settle a feature back to its base size. We set hoverProgress to 0 rather than calling
    // removeFeatureState: removeFeatureState can crash MapLibre's render (coalesceChanges)
    // after rapid per-frame feature-state updates, and the paint reads an unset state as 0
    // anyway, so 0 is visually identical.
    const clearState = (id: string) => {
        setState(id, 0);
    };

    const step = (ts: number) => {
        const dt = lastTs == null ? 0 : ts - lastTs;
        lastTs = ts;
        const delta = dt / HOVER_TWEEN_MS;

        let animating = false;
        for (const [id, to] of target) {
            let p = progress.get(id) ?? 0;
            if (p < to) p = Math.min(to, p + delta);
            else if (p > to) p = Math.max(to, p - delta);
            progress.set(id, p);

            if (p === to && to === 0) {
                // Fully ramped out — settle to 0 so the feature renders at its base size.
                progress.delete(id);
                target.delete(id);
                clearState(id);
            } else {
                setState(id, p);
                if (p !== to) animating = true;
            }
        }

        if (animating) {
            rafId = requestAnimationFrame(step);
        } else {
            rafId = null;
            lastTs = null;
        }
    };

    const ensureRunning = () => {
        if (rafId == null) {
            lastTs = null;
            rafId = requestAnimationFrame(step);
        }
    };

    return {
        setHovered(id: string | null) {
            if (id === hoveredId) return;
            if (hoveredId != null) target.set(hoveredId, 0);
            if (id != null) {
                target.set(id, 1);
                if (!progress.has(id)) progress.set(id, 0);
            }
            hoveredId = id;
            ensureRunning();
        },
        destroy() {
            if (rafId != null) cancelAnimationFrame(rafId);
            rafId = null;
            lastTs = null;
            for (const id of progress.keys()) clearState(id);
            progress.clear();
            target.clear();
            hoveredId = null;
        },
    };
}
